// Cabecera y pie compartidos de los impresos médicos (receta y orden de
// exámenes), con las etiquetas del país de la empresa.
//
// El formato es el MISMO en Ecuador, Perú y Colombia — lo que cambia son tres
// palabras que en el país equivocado son un error de forma:
//   · el documento de identidad: CI / DNI / CC,
//   · el registro del médico: Lic. / CMP / RM,
//   · el formato de fecha.
//
// El país viene de la RPC (`empresa_pais`), no de la sesión: así el impreso
// sale bien aunque se genere desde un listado de otra pantalla, y sigue siendo
// correcto si mañana cambia la forma del token.
import { ReactNode } from 'react';
import { LOCALE } from '@/lib/utils/regional';

/** Lo que toda RPC de impreso médico devuelve de la empresa (script 851). */
export interface CabeceraImpreso {
  empresa: string;
  empresa_pais: string | null;
  empresa_ruc?: string | null;
  empresa_direccion?: string | null;
  empresa_telefono?: string | null;
  establecimiento_ciudad?: string | null;
}

const ID_POR_PAIS: Record<string, string> = { EC: 'CI', PE: 'DNI', CO: 'CC' };
const REG_POR_PAIS: Record<string, string> = { EC: 'Lic.', PE: 'CMP', CO: 'RM' };
const LOCALE_POR_PAIS: Record<string, string> = { EC: LOCALE, PE: 'es-PE', CO: 'es-CO' };

/** Cómo se llama el documento de identidad del paciente en ese país. */
export const etiquetaIdentificacion = (pais?: string | null) => ID_POR_PAIS[pais ?? 'EC'] ?? 'ID';
/** Cómo se llama el registro profesional del médico en ese país. */
export const etiquetaRegistro = (pais?: string | null) => REG_POR_PAIS[pais ?? 'EC'] ?? 'Reg.';
export const localeDe = (pais?: string | null) => LOCALE_POR_PAIS[pais ?? 'EC'] ?? LOCALE;

/** Fecha corta en el formato del país de la empresa. */
export const fechaPais = (valor: string | Date, pais?: string | null) =>
  new Date(valor).toLocaleDateString(localeDe(pais));

export const edadTexto = (anios?: number | null, meses?: number | null) =>
  anios != null ? `${anios} años${meses ? ` ${meses} meses` : ''}` : '—';

/**
 * Encabezado del impreso: empresa + logo, fecha con ciudad, y el bloque del
 * paciente. `derecha` es lo que identifica al documento (nº de receta, nº de
 * orden…), que es lo único que cambia entre documentos.
 */
export function EncabezadoImpreso({ cab, logo, fecha: fechaDoc, derecha, paciente, identificacion, edad }: {
  cab: CabeceraImpreso;
  logo?: string | null;
  /** La del DOCUMENTO, no la de hoy: una receta impresa mañana sigue siendo de ayer. */
  fecha: string | Date;
  derecha?: ReactNode;
  paciente: string;
  identificacion?: string | null;
  edad?: ReactNode;
}) {
  const ciudad = (cab.establecimiento_ciudad || '').trim().toUpperCase();
  const fecha = fechaPais(fechaDoc, cab.empresa_pais);
  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[13px] font-semibold">{cab.empresa}</p>
          {(cab.empresa_direccion || cab.empresa_telefono) && (
            <p className="text-[9px] text-gray-600">
              {[cab.empresa_direccion, cab.empresa_telefono].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        {logo && <img src={logo} alt="" className="h-9 max-w-[120px] object-contain" />}
      </div>
      <hr className="my-1 border-black" />
      <div className="flex justify-between text-[11px]">
        <p><b>Fecha:</b> {ciudad ? `${ciudad}, ` : ''}{fecha}</p>
        {derecha && <p>{derecha}</p>}
      </div>
      <p className="text-[11px]"><b>Paciente:</b> {paciente}</p>
      <div className="flex justify-between text-[11px]">
        <p><b>{etiquetaIdentificacion(cab.empresa_pais)}:</b> {identificacion ?? 's/i'}</p>
        {edad != null && <p><b>Edad:</b> {edad}</p>}
      </div>
      <hr className="my-1 border-black" />
    </>
  );
}

/**
 * Pie de firma. `marca` es lo que va encima de la línea: la rúbrica escaneada,
 * el QR de verificación, o nada (espacio para firmar a mano).
 */
export function FirmaImpreso({ marca, medico, licencia, especialidad, pais }: {
  marca?: ReactNode;
  medico: string;
  licencia?: string | null;
  especialidad?: string | null;
  pais?: string | null;
}) {
  return (
    <div className="mt-6 text-center text-[10px]">
      {marca ?? <div className="h-9" />}
      <div className="mx-auto mt-1 max-w-[85%] border-t px-4 pt-1">
        {medico}{licencia ? ` · ${etiquetaRegistro(pais)} ${licencia}` : ''}
      </div>
      {especialidad && <p className="text-gray-500">{especialidad}</p>}
    </div>
  );
}

/** Título de la orden según lo que se pidió. Si mezcla, es una orden general. */
export function tituloOrden(categorias: string[]) {
  const unicas = Array.from(new Set(categorias));
  if (unicas.length === 1) {
    switch (unicas[0]) {
      case 'laboratorio': return 'ORDEN DE LABORATORIO';
      case 'imagenologia': return 'ORDEN DE IMAGENOLOGÍA';
      case 'patologia': return 'ORDEN DE ANATOMÍA PATOLÓGICA';
      case 'microbiologia': return 'ORDEN DE MICROBIOLOGÍA';
      case 'cardiologia': return 'ORDEN DE CARDIOLOGÍA';
      default: return 'ORDEN DE EXÁMENES';
    }
  }
  return 'ORDEN DE EXÁMENES';
}

export const NOMBRE_CATEGORIA: Record<string, string> = {
  laboratorio: 'Laboratorio clínico',
  imagenologia: 'Imagenología',
  patologia: 'Anatomía patológica',
  microbiologia: 'Microbiología',
  cardiologia: 'Cardiología',
  otro: 'Otros estudios',
};
